import { z } from 'zod'
import type { AITool } from './types'
import { fuzzyScore, normalizeText } from '../fuzzy-search'

const parametersSchema = z.object({
  query: z.string()
    .min(1)
    .describe('Exercise name to search for'),
  limit: z.number()
    .min(1)
    .max(10)
    .describe('Maximum number of results to return (1-10, default 5)')
})

// Common exercise abbreviations
const ABBREVIATIONS: Record<string, string> = {
  'bb': 'barbell',
  'db': 'dumbbell',
  'ez': 'ez bar',
  'ohp': 'overhead press',
  'rdl': 'romanian deadlift',
  'sldl': 'stiff leg deadlift',
  'bp': 'bench press',
  'dl': 'deadlift',
  'sq': 'squat',
  'pr': 'press',
  'flys': 'flies',
  'tris': 'triceps',
  'bis': 'biceps',
  'delts': 'deltoids',
  'lats': 'latissimus'
}

function expandAbbreviations(query: string): string {
  let expanded = query.toLowerCase()
  
  Object.entries(ABBREVIATIONS).forEach(([abbr, full]) => {
    const regex = new RegExp(`\\b${abbr}\\b`, 'gi')
    expanded = expanded.replace(regex, full)
  })
  
  return expanded
}

export const searchExerciseLibrary: AITool = {
  name: 'searchExerciseLibrary',
  description: 'Search all available exercises in the library.',
  parameters: parametersSchema,
  
  execute: async (args, context) => {
    const { query, limit = 5 } = args
    const { exerciseTemplates, userSettings } = context
    
    if (!exerciseTemplates || exerciseTemplates.length === 0) {
      return {
        summary: 'No exercise templates available in the library.',
        query,
        matches: [],
        totalAvailable: 0
      }
    }
    
    // Expand abbreviations in query
    const expandedQuery = expandAbbreviations(query)
    
    // Also try reversing "Equipment Exercise" to "Exercise (Equipment)" format
    const words = expandedQuery.split(' ')
    let alternativeQuery = expandedQuery
    
    const equipmentWords = ['barbell', 'dumbbell', 'cable', 'machine', 'smith', 'ez', 'kettlebell', 'band', 'bodyweight']
    if (words.length > 1 && equipmentWords.includes(words[0])) {
      alternativeQuery = words.slice(1).concat(words[0]).join(' ')
    }
    
    // Score all exercises
    const scoredExercises = exerciseTemplates.map(exercise => {
      // Try scoring with original, expanded, and alternative queries
      const originalScore = fuzzyScore(query, exercise.name)
      const expandedScore = fuzzyScore(expandedQuery, exercise.name)
      const alternativeScore = fuzzyScore(alternativeQuery, exercise.name)
      const bestScore = Math.max(originalScore, expandedScore, alternativeScore)
      
      // Boost score for exact word matches
      const normalizedQuery = normalizeText(query)
      const normalizedName = normalizeText(exercise.name)
      const queryWords = normalizedQuery.split(' ')
      
      let wordBonus = 0
      queryWords.forEach(word => {
        if (normalizedName.split(' ').includes(word)) {
          wordBonus += 10
        }
      })
      
      const finalScore = Math.min(100, bestScore + wordBonus)
      
      return {
        templateId: exercise.id,
        name: exercise.name,
        score: finalScore,
        primaryMover: exercise.primaryMover || 'Unknown',
        secondaryMovers: exercise.secondaryMovers || [],
        equipment: exercise.name.match(/\(([^)]+)\)/)?.[1] || 'Various'
      }
    })
    
    // Filter and sort matches
    const matches = scoredExercises
      .filter(match => match.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
    
    return {
      query,
      matchCount: scoredExercises.filter(e => e.score > 0).length,
      matches: matches.map(m => ({
        templateId: m.templateId,
        name: m.name,
        score: m.score,
        primaryMover: m.primaryMover,
        equipment: m.equipment
      })),
      totalAvailable: exerciseTemplates.length
    }
  }
}